import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Modal } from "@/components/Modal";
import { Input } from "@/components/Input";
import { Switch } from "@/components/Switch";
import { useEditor, EditorContent } from "@tiptap/react";
import { Extension, type CommandProps } from "@tiptap/core";
import { StarterKit } from "@tiptap/starter-kit";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { FontFamily } from "@tiptap/extension-font-family";
import ImageResize from "tiptap-extension-resize-image";
import { Highlight } from "@tiptap/extension-highlight";
import { TextAlign } from "@tiptap/extension-text-align";
import { Subscript } from "@tiptap/extension-subscript";
import { Superscript } from "@tiptap/extension-superscript";
import Placeholder from "@tiptap/extension-placeholder";
import { Icon } from "@/components/Icon";
import { textToHtml } from "@/utils/format";

declare module "@tiptap/core" {
    interface Commands<ReturnType> {
        indentation: { indent: () => ReturnType; outdent: () => ReturnType };
    }
}

export interface RichTextFullProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    minHeight?: string;
    autoFocus?: boolean;
    rounded?: boolean;
    hideFullscreen?: boolean;
    onFullscreenClick?: () => void;
}

const FontSize = Extension.create({
    name: "fontSize",
    addOptions() {
        return {
            types: ["textStyle"],
        };
    },
    addGlobalAttributes() {
        return [
            {
                types: this.options.types,
                attributes: {
                    fontSize: {
                        default: null,
                        parseHTML: (element) =>
                            element.style.fontSize.replace(/['"]+/g, ""),
                        renderHTML: (attributes) => {
                            if (!attributes.fontSize) {
                                return {};
                            }
                            return {
                                style: `font-size: ${attributes.fontSize}`,
                            };
                        },
                    },
                },
            },
        ];
    },
    addCommands() {
        return {
            setFontSize:
                (fontSize: string) =>
                ({ chain }: CommandProps) => {
                    return chain().setMark("textStyle", { fontSize }).run();
                },
            unsetFontSize:
                () =>
                ({ chain }: CommandProps) => {
                    return chain()
                        .setMark("textStyle", { fontSize: null })
                        .removeEmptyTextStyle()
                        .run();
                },
        };
    },
});

const Indent = Extension.create({
    name: "indent",
    addOptions() {
        return {
            types: ["paragraph", "heading", "blockquote"],
            minLevel: 0,
            maxLevel: 8,
        };
    },
    addGlobalAttributes() {
        return [
            {
                types: this.options.types,
                attributes: {
                    indent: {
                        default: 0,
                        parseHTML: (element) =>
                            parseInt(element.style.paddingLeft, 10) || 0,
                        renderHTML: (attributes) => {
                            if (!attributes.indent) {
                                return {};
                            }
                            return {
                                style: `padding-left: ${attributes.indent * 20}px`,
                            };
                        },
                    },
                },
            },
        ];
    },
    addCommands() {
        return {
            indent:
                () =>
                ({ tr, state, dispatch }: CommandProps) => {
                    const { selection } = state;
                    const { from, to } = selection;
                    let changed = false;
                    state.doc.nodesBetween(from, to, (node, pos) => {
                        if (this.options.types.includes(node.type.name)) {
                            const indent =
                                ((node.attrs as Record<string, unknown>)
                                    .indent as number) || 0;
                            if (indent < this.options.maxLevel) {
                                tr = tr.setNodeMarkup(pos, null, {
                                    ...node.attrs,
                                    indent: indent + 1,
                                });
                                changed = true;
                            }
                        }
                    });
                    if (changed && dispatch) {
                        dispatch(tr);
                        return true;
                    }
                    return false;
                },
            outdent:
                () =>
                ({ tr, state, dispatch }: CommandProps) => {
                    const { selection } = state;
                    const { from, to } = selection;
                    let changed = false;
                    state.doc.nodesBetween(from, to, (node, pos) => {
                        if (this.options.types.includes(node.type.name)) {
                            const indent =
                                ((node.attrs as Record<string, unknown>)
                                    .indent as number) || 0;
                            if (indent > this.options.minLevel) {
                                tr = tr.setNodeMarkup(pos, null, {
                                    ...node.attrs,
                                    indent: indent - 1,
                                });
                                changed = true;
                            }
                        }
                    });
                    if (changed && dispatch) {
                        dispatch(tr);
                        return true;
                    }
                    return false;
                },
        };
    },
});

const FONT_FAMILIES: string[] = [
    "Arial",
    "Courier New",
    "Georgia",
    "Lucida Sans Unicode",
    "Tahoma",
    "Times New Roman",
    "Trebuchet MS",
    "Verdana",
];

const FONT_SIZES: string[] = [
    "10px",
    "12px",
    "14px",
    "16px",
    "18px",
    "20px",
    "24px",
    "32px",
    "40px",
];

const COLOR_PALETTE: string[] = [
    "#000000",
    "#434343",
    "#666666",
    "#999999",
    "#b7b7b7",
    "#cccccc",
    "#d9d9d9",
    "#efefef",
    "#f3f3f3",
    "#ffffff",
    "#980000",
    "#ff0000",
    "#ff9900",
    "#ffff00",
    "#00ff00",
    "#00ffff",
    "#4a86e8",
    "#0000ff",
    "#9900ff",
    "#ff00ff",
    "#e6b8af",
    "#f4cccc",
    "#fce5cd",
    "#fff2cc",
    "#d9ead3",
    "#d0e0e3",
    "#c9daf8",
    "#cfe2f3",
    "#d9d2e9",
    "#ead1dc",
    "#dd7e6b",
    "#ea9999",
    "#f9cb9c",
    "#ffe599",
    "#b6d7a8",
    "#a2c4c9",
    "#a4c2f4",
    "#9fc5e8",
    "#b4a7d6",
    "#d5a6bd",
    "#cc4125",
    "#e06666",
    "#f6b26b",
    "#ffd966",
    "#93c47d",
    "#76a5af",
    "#6d9eeb",
    "#6fa8dc",
    "#8e7cc3",
    "#c27ba0",
];

const COMMON_SYMBOLS: string[] = [
    "©",
    "®",
    "™",
    "§",
    "¶",
    "†",
    "‡",
    "•",
    "←",
    "↑",
    "→",
    "↓",
    "↔",
    "↵",
    "⇐",
    "⇑",
    "⇒",
    "⇓",
    "⇔",
    "∀",
    "∂",
    "∃",
    "∅",
    "∇",
    "∈",
    "∉",
    "∋",
    "∏",
    "∑",
    "−",
    "∗",
    "√",
    "∝",
    "∞",
    "∠",
    "∧",
    "∨",
    "∩",
    "∪",
    "∫",
    "∴",
    "∼",
    "≅",
    "≈",
    "≠",
    "≡",
    "≤",
    "≥",
    "⊂",
    "⊃",
    "⊄",
    "⊈",
    "⊉",
    "⊕",
    "⊗",
    "⊥",
    "⋅",
    "⌈",
    "⌉",
    "⌊",
    "⌋",
];

const COMMON_EMOJIS: string[] = [
    "😀",
    "😃",
    "😄",
    "😁",
    "😆",
    "😅",
    "😂",
    "🤣",
    "😊",
    "😇",
    "🙂",
    "🙃",
    "😉",
    "😌",
    "😍",
    "🥰",
    "😘",
    "😗",
    "😙",
    "😚",
    "😋",
    "😛",
    "😜",
    "🤪",
    "😝",
    "🤑",
    "🤗",
    "🤭",
    "🤫",
    "🤔",
    "🤐",
    "🤨",
    "😐",
    "😑",
    "😶",
    "😏",
    "😒",
    "🙄",
    "😬",
    "🤥",
    "😔",
    "😪",
    "🤤",
    "😴",
    "😷",
    "🤒",
    "🤕",
    "🤢",
    "🤮",
    "🤧",
    "🥵",
    "🥶",
    "🥴",
    "😵",
    "🤯",
    "🤠",
    "🥳",
    "😎",
    "🤓",
    "🧐",
    "😕",
    "😟",
    "🙁",
    "😮",
    "😯",
    "😲",
    "😳",
    "🥺",
    "😦",
    "😧",
    "😨",
    "😰",
    "😥",
    "😢",
    "😭",
    "😱",
    "😖",
    "😣",
    "😞",
    "😓",
    "😩",
    "😫",
    "🥱",
    "😤",
    "😡",
    "😠",
    "🤬",
    "👍",
    "👎",
    "👏",
    "🙌",
    "🚀",
    "🔥",
    "🎉",
    "💡",
    "✅",
    "❌",
    "⚠️",
    "📌",
    "📎",
];

export const RichTextFull: React.FC<RichTextFullProps> = ({
    value,
    onChange,
    placeholder = "",
    disabled = false,
    className = "",
    minHeight = "200px",
    autoFocus = false,
    rounded = false,
    hideFullscreen = false,
    onFullscreenClick,
}: RichTextFullProps) => {
    const { t } = useTranslation("global");
    const containerRef = useRef<HTMLDivElement>(null);
    const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
    const [isSourceMode, setIsSourceMode] = useState<boolean>(false);
    const [sourceContent, setSourceContent] = useState<string>("");
    const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
    const [hoveredTable, setHoveredTable] = useState<{
        rows: number;
        cols: number;
    }>({ rows: 0, cols: 0 });
    const [linkModalOpen, setLinkModalOpen] = useState<boolean>(false);
    const [linkUrl, setLinkUrl] = useState<string>("");
    const [linkText, setLinkText] = useState<string>("");
    const [linkNewTab, setLinkNewTab] = useState<boolean>(true);
    const [imageModalOpen, setImageModalOpen] = useState<boolean>(false);
    const [imageUrlInput, setImageUrlInput] = useState<string>("");
    const [isImageDragOver, setIsImageDragOver] = useState<boolean>(false);

    const editor = useEditor({
        editable: !disabled,
        autofocus: autoFocus,
        editorProps: {
            attributes: {
                class: "richtext-editor focus:outline-none outline-none flex-1 min-h-[200px] text-sm",
                spellcheck: "false",
            },
        },
        extensions: [
            StarterKit.configure({
                bulletList: { keepMarks: true, keepAttributes: false },
                orderedList: { keepMarks: true, keepAttributes: false },
                horizontalRule: {},
                undoRedo: {},
                dropcursor: false,
                link: {
                    openOnClick: false,
                    HTMLAttributes: {
                        rel: "noopener noreferrer",
                        target: "_blank",
                    },
                },
                underline: {},
            }),
            TextAlign.configure({
                types: ["heading", "paragraph", "image"],
            }),
            Table.configure({
                resizable: true,
            }),
            TableRow,
            TableHeader,
            TableCell,
            TextStyle,
            Color,
            FontFamily,
            FontSize,
            Indent,
            ImageResize.extend({
                renderHTML({ HTMLAttributes }) {
                    // Extract existing styles and container style
                    const style = HTMLAttributes.style || "";
                    const containerStyle =
                        HTMLAttributes.containerstyle ||
                        HTMLAttributes.containerStyle ||
                        "";

                    let extraStyles = "";
                    if (
                        style.includes("text-align: center") ||
                        containerStyle.includes("margin: 0 auto") ||
                        containerStyle.includes("margin: 0px auto")
                    ) {
                        extraStyles =
                            "display: block; margin-left: auto; margin-right: auto;";
                    } else if (
                        style.includes("text-align: right") ||
                        containerStyle.includes("margin: 0 0 0 auto") ||
                        containerStyle.includes("margin-left: auto")
                    ) {
                        extraStyles =
                            "display: block; margin-left: auto; margin-right: 0;";
                    }

                    // Keep original attributes but merge our standard alignment styles so browsers render it natively
                    return [
                        "img",
                        {
                            ...HTMLAttributes,
                            style: `${style} ${extraStyles}`.trim(),
                        },
                    ];
                },
            }).configure({
                inline: false,
                allowBase64: true,
            }),
            Highlight.configure({ multicolor: true }),
            Subscript,
            Superscript,
            Placeholder.configure({
                placeholder: placeholder || t("editor.paragraph"),
                emptyEditorClass: "is-editor-empty",
            }),
        ],
        content: textToHtml(value || ""),
        onUpdate: ({ editor: currentEditor }) => {
            onChange(currentEditor.isEmpty ? "" : currentEditor.getHTML());
        },
    });

    useEffect(() => {
        if (!editor) {
            return;
        }

        if (editor.isFocused) {
            return;
        }

        const currentHTML = editor.getHTML();
        const normalized = textToHtml(value || "");
        if (normalized !== currentHTML && (value || "") !== currentHTML) {
            editor.commands.setContent(normalized, { emitUpdate: false });
        }
    }, [value, editor]);

    useEffect(() => {
        if (editor) {
            editor.setEditable(!disabled);
        }
    }, [disabled, editor]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent): void => {
            if (
                containerRef.current &&
                !containerRef.current.contains(e.target as Node)
            ) {
                setActiveDropdown(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const toggleDropdown = (name: string): void => {
        setActiveDropdown((prev) => (prev === name ? null : name));
    };

    const handleApplyColor = (
        color: string | null,
        isBackground = false
    ): void => {
        if (!editor) {
            return;
        }

        if (isBackground) {
            if (color) {
                editor.chain().focus().setHighlight({ color }).run();
            } else {
                editor.chain().focus().unsetHighlight().run();
            }
        } else {
            if (color) {
                editor.chain().focus().setColor(color).run();
            } else {
                editor.chain().focus().unsetColor().run();
            }
        }
        setActiveDropdown(null);
    };

    const handleInsertTable = (rows: number, cols: number): void => {
        if (!editor) {
            return;
        }

        editor
            .chain()
            .focus()
            .insertTable({ rows, cols, withHeaderRow: true })
            .run();
        setActiveDropdown(null);
        setHoveredTable({ rows: 0, cols: 0 });
    };

    const openLinkDialog = (): void => {
        if (!editor) {
            return;
        }

        const { selection } = editor.state;
        const selectedText = editor.state.doc.textBetween(
            selection.from,
            selection.to
        );
        const attrs = editor.getAttributes("link");
        setLinkUrl(attrs.href || "");
        setLinkText(selectedText || "");
        setLinkNewTab(attrs.target === "_blank");
        setLinkModalOpen(true);
        setActiveDropdown(null);
    };

    const applyLink = (): void => {
        if (!editor) {
            return;
        }

        if (!linkUrl.trim()) {
            editor.chain().focus().unsetLink().run();
        } else {
            if (linkText.trim() && editor.state.selection.empty) {
                editor
                    .chain()
                    .focus()
                    .insertContent(
                        `<a href="${linkUrl}" ${linkNewTab ? 'target="_blank" rel="noopener noreferrer"' : ""}>${linkText}</a>`
                    )
                    .run();
            } else {
                editor
                    .chain()
                    .focus()
                    .extendMarkRange("link")
                    .setLink({
                        href: linkUrl,
                        target: linkNewTab ? "_blank" : null,
                    })
                    .run();
            }
        }
        setLinkModalOpen(false);
    };

    const handleInsertImage = (): void => {
        if (!editor || !imageUrlInput.trim()) {
            return;
        }

        editor.chain().focus().setImage({ src: imageUrlInput.trim() }).run();
        setImageUrlInput("");
        setImageModalOpen(false);
    };

    const processImageFile = (file: File): void => {
        if (!editor) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const result = event.target?.result;
            if (typeof result === "string") {
                editor.chain().focus().setImage({ src: result }).run();
                setImageModalOpen(false);
            }
        };
        reader.readAsDataURL(file);
    };

    const handleImageFileChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ): void => {
        const file = e.target.files?.[0];
        if (!file) return;
        processImageFile(file);
    };

    const handleImageDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
        e.preventDefault();
        setIsImageDragOver(true);
    };

    const handleImageDragLeave = (): void => {
        setIsImageDragOver(false);
    };

    const handleImageDrop = (e: React.DragEvent<HTMLDivElement>): void => {
        e.preventDefault();
        setIsImageDragOver(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const file = e.dataTransfer.files[0];
            if (file.type.startsWith("image/")) {
                processImageFile(file);
            }
        }
    };

    const toggleSourceMode = (): void => {
        if (!editor) {
            return;
        }

        if (!isSourceMode) {
            setSourceContent(editor.getHTML());
        } else {
            editor.commands.setContent(sourceContent);
            onChange(sourceContent);
        }
        setIsSourceMode(!isSourceMode);
    };

    const toggleFullScreen = (): void => {
        if (isFullScreen && isSourceMode) {
            toggleSourceMode();
        }
        setIsFullScreen((prev) => !prev);
    };

    if (!editor) {
        return null;
    }

    const editorContent = (
        <div
            ref={containerRef}
            style={{
                minHeight: isFullScreen ? undefined : minHeight,
                resize: isFullScreen ? "none" : "vertical",
            }}
            className={`${
                isFullScreen
                    ? `w-full h-full bg-white dark:bg-dark-box flex flex-col ${rounded ? "rounded-2xl!" : ""} ${className}`
                    : `relative tiptap-container mt-[1px] dark:bg-dark-box border border-slate-200! dark:border-white/20! flex flex-col resize-y ${rounded ? "rounded-2xl!" : ""} ${className}`
            }`}
        >
            {/* Top Menu Bar: File, Edit, View, Insert, Format, Table */}
            <div className="text-center tiptap-menu-bar-container select-none">
                <div
                    className={`tiptap-top-menu border-b border-slate-200 dark:border-white/10 bg-slate-50/80 dark:bg-dark-subtle/80 text-xs${rounded ? " rounded-t-2xl" : ""}`}
                >
                    {/* File menu */}
                    <div className="relative inline-block">
                        <button
                            type="button"
                            onClick={() => toggleDropdown("menu-file")}
                            className="menu-button hover:bg-slate-200/60 dark:hover:bg-white/10 rounded px-2 py-1 text-slate-700 dark:text-zinc-300 font-medium"
                        >
                            {t("editor.menu.file")}
                        </button>
                        {activeDropdown === "menu-file" && (
                            <div className="absolute left-0 top-full mt-1 w-44 rounded-lg bg-white dark:bg-dark-box shadow-xl border border-slate-200 dark:border-white/15 p-1 z-50 text-xs">
                                <button
                                    type="button"
                                    onClick={() => {
                                        editor.commands.clearContent();
                                        setActiveDropdown(null);
                                    }}
                                    className="w-full text-left px-2.5 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-white/10 flex items-center gap-2 text-slate-700 dark:text-zinc-200"
                                >
                                    <Icon value="fileText" size={13} />
                                    <span>{t("editor.new_document")}</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        window.print();
                                        setActiveDropdown(null);
                                    }}
                                    className="w-full text-left px-2.5 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-white/10 flex items-center gap-2 text-slate-700 dark:text-zinc-200"
                                >
                                    <Icon value="download" size={13} />
                                    <span>{t("editor.print")}</span>
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Edit menu */}
                    <div className="relative inline-block">
                        <button
                            type="button"
                            onClick={() => toggleDropdown("menu-edit")}
                            className="menu-button hover:bg-slate-200/60 dark:hover:bg-white/10 rounded px-2 py-1 text-slate-700 dark:text-zinc-300 font-medium"
                        >
                            {t("editor.menu.edit")}
                        </button>
                        {activeDropdown === "menu-edit" && (
                            <div className="absolute left-0 top-full mt-1 w-44 rounded-lg bg-white dark:bg-dark-box shadow-xl border border-slate-200 dark:border-white/15 p-1 z-50 text-xs">
                                <button
                                    type="button"
                                    disabled={!editor.can().undo()}
                                    onClick={() => {
                                        editor.chain().focus().undo().run();
                                        setActiveDropdown(null);
                                    }}
                                    className="w-full text-left px-2.5 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-white/10 disabled:opacity-40 flex items-center gap-2 text-slate-700 dark:text-zinc-200"
                                >
                                    <Icon value="undo" size={13} />
                                    <span>{t("editor.undo")}</span>
                                </button>
                                <button
                                    type="button"
                                    disabled={!editor.can().redo()}
                                    onClick={() => {
                                        editor.chain().focus().redo().run();
                                        setActiveDropdown(null);
                                    }}
                                    className="w-full text-left px-2.5 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-white/10 disabled:opacity-40 flex items-center gap-2 text-slate-700 dark:text-zinc-200"
                                >
                                    <Icon value="redo" size={13} />
                                    <span>{t("editor.redo")}</span>
                                </button>
                                <div className="h-px bg-slate-200 dark:bg-white/10 my-1" />
                                <button
                                    type="button"
                                    onClick={() => {
                                        editor
                                            .chain()
                                            .focus()
                                            .selectAll()
                                            .run();
                                        setActiveDropdown(null);
                                    }}
                                    className="w-full text-left px-2.5 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-white/10 flex items-center gap-2 text-slate-700 dark:text-zinc-200"
                                >
                                    <Icon value="check" size={13} />
                                    <span>{t("editor.select_all")}</span>
                                </button>
                            </div>
                        )}
                    </div>

                    {/* View menu */}
                    <div className="relative inline-block">
                        <button
                            type="button"
                            onClick={() => toggleDropdown("menu-view")}
                            className="menu-button hover:bg-slate-200/60 dark:hover:bg-white/10 rounded px-2 py-1 text-slate-700 dark:text-zinc-300 font-medium"
                        >
                            {t("editor.menu.view")}
                        </button>
                        {activeDropdown === "menu-view" && (
                            <div className="absolute left-0 top-full mt-1 w-44 rounded-lg bg-white dark:bg-dark-box shadow-xl border border-slate-200 dark:border-white/15 p-1 z-50 text-xs">
                                {!hideFullscreen && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (onFullscreenClick)
                                                onFullscreenClick();
                                            else toggleFullScreen();
                                            setActiveDropdown(null);
                                        }}
                                        className="w-full text-left px-2.5 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-white/10 flex items-center gap-2 text-slate-700 dark:text-zinc-200"
                                    >
                                        <Icon
                                            value={
                                                isFullScreen
                                                    ? "minimize"
                                                    : "maximize"
                                            }
                                            size={13}
                                        />
                                        <span>{t("editor.fullscreen")}</span>
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => {
                                        toggleSourceMode();
                                        setActiveDropdown(null);
                                    }}
                                    className="w-full text-left px-2.5 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-white/10 flex items-center gap-2 text-slate-700 dark:text-zinc-200"
                                >
                                    <Icon value="code" size={13} />
                                    <span>
                                        {isSourceMode
                                            ? t("editor.rich_text")
                                            : t("editor.source_code")}
                                    </span>
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Insert menu */}
                    <div className="relative inline-block">
                        <button
                            type="button"
                            onClick={() => toggleDropdown("menu-insert")}
                            className="menu-button hover:bg-slate-200/60 dark:hover:bg-white/10 rounded px-2 py-1 text-slate-700 dark:text-zinc-300 font-medium"
                        >
                            {t("editor.menu.insert")}
                        </button>
                        {activeDropdown === "menu-insert" && (
                            <div className="absolute left-0 top-full mt-1 w-44 rounded-lg bg-white dark:bg-dark-box shadow-xl border border-slate-200 dark:border-white/15 p-1 z-50 text-xs">
                                <button
                                    type="button"
                                    onClick={() => {
                                        openLinkDialog();
                                        setActiveDropdown(null);
                                    }}
                                    className="w-full text-left px-2.5 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-white/10 flex items-center gap-2 text-slate-700 dark:text-zinc-200"
                                >
                                    <Icon value="link" size={13} />
                                    <span>{t("editor.link")}</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setImageUrlInput("");
                                        setImageModalOpen(true);
                                        setActiveDropdown(null);
                                    }}
                                    className="w-full text-left px-2.5 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-white/10 flex items-center gap-2 text-slate-700 dark:text-zinc-200"
                                >
                                    <Icon value="image" size={13} />
                                    <span>{t("editor.image")}</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        editor
                                            .chain()
                                            .focus()
                                            .setHorizontalRule()
                                            .run();
                                        setActiveDropdown(null);
                                    }}
                                    className="w-full text-left px-2.5 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-white/10 flex items-center gap-2 text-slate-700 dark:text-zinc-200"
                                >
                                    <Icon value="minus" size={13} />
                                    <span>{t("editor.horizontal_line")}</span>
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Format menu */}
                    <div className="relative inline-block">
                        <button
                            type="button"
                            onClick={() => toggleDropdown("menu-format")}
                            className="menu-button hover:bg-slate-200/60 dark:hover:bg-white/10 rounded px-2 py-1 text-slate-700 dark:text-zinc-300 font-medium"
                        >
                            {t("editor.menu.format")}
                        </button>
                        {activeDropdown === "menu-format" && (
                            <div className="absolute left-0 top-full mt-1 w-44 rounded-lg bg-white dark:bg-dark-box shadow-xl border border-slate-200 dark:border-white/15 p-1 z-50 text-xs">
                                <button
                                    type="button"
                                    onClick={() => {
                                        editor
                                            .chain()
                                            .focus()
                                            .toggleBold()
                                            .run();
                                        setActiveDropdown(null);
                                    }}
                                    className="w-full text-left px-2.5 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-white/10 flex items-center gap-2 text-slate-700 dark:text-zinc-200 font-bold"
                                >
                                    <Icon value="bold" size={13} />
                                    <span>{t("editor.bold")}</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        editor
                                            .chain()
                                            .focus()
                                            .toggleItalic()
                                            .run();
                                        setActiveDropdown(null);
                                    }}
                                    className="w-full text-left px-2.5 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-white/10 flex items-center gap-2 text-slate-700 dark:text-zinc-200 italic"
                                >
                                    <Icon value="italic" size={13} />
                                    <span>{t("editor.italic")}</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        editor
                                            .chain()
                                            .focus()
                                            .toggleUnderline()
                                            .run();
                                        setActiveDropdown(null);
                                    }}
                                    className="w-full text-left px-2.5 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-white/10 flex items-center gap-2 text-slate-700 dark:text-zinc-200 underline"
                                >
                                    <Icon value="underline" size={13} />
                                    <span>{t("editor.underline")}</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        editor
                                            .chain()
                                            .focus()
                                            .toggleStrike()
                                            .run();
                                        setActiveDropdown(null);
                                    }}
                                    className="w-full text-left px-2.5 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-white/10 flex items-center gap-2 text-slate-700 dark:text-zinc-200 line-through"
                                >
                                    <Icon value="strike" size={13} />
                                    <span>{t("editor.strike")}</span>
                                </button>
                                <div className="h-px bg-slate-200 dark:bg-white/10 my-1" />
                                <button
                                    type="button"
                                    onClick={() => {
                                        editor
                                            .chain()
                                            .focus()
                                            .unsetAllMarks()
                                            .clearNodes()
                                            .run();
                                        setActiveDropdown(null);
                                    }}
                                    className="w-full text-left px-2.5 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-white/10 flex items-center gap-2 text-slate-700 dark:text-zinc-200"
                                >
                                    <Icon value="removeFormatting" size={13} />
                                    <span>{t("editor.remove_format")}</span>
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Table menu */}
                    <div className="relative inline-block">
                        <button
                            type="button"
                            onClick={() => toggleDropdown("menu-table")}
                            className="menu-button hover:bg-slate-200/60 dark:hover:bg-white/10 rounded px-2 py-1 text-slate-700 dark:text-zinc-300 font-medium"
                        >
                            {t("editor.menu.table")}
                        </button>
                        {activeDropdown === "menu-table" && (
                            <div className="absolute left-0 top-full mt-1 w-52 rounded-lg bg-white dark:bg-dark-box shadow-xl border border-slate-200 dark:border-white/15 p-1 z-50 text-xs">
                                <button
                                    type="button"
                                    onClick={() => handleInsertTable(3, 3)}
                                    className="w-full text-left px-2.5 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-white/10 flex items-center gap-2 text-slate-700 dark:text-zinc-200"
                                >
                                    <Icon value="table" size={13} />
                                    <span>
                                        {t("editor.insert_table")} (3x3)
                                    </span>
                                </button>
                                <div className="h-px bg-slate-200 dark:bg-white/10 my-1" />
                                <button
                                    type="button"
                                    disabled={!editor.isActive("table")}
                                    onClick={() => {
                                        editor
                                            .chain()
                                            .focus()
                                            .addRowBefore()
                                            .run();
                                        setActiveDropdown(null);
                                    }}
                                    className="w-full text-left px-2.5 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-white/10 disabled:opacity-40 text-slate-700 dark:text-zinc-200"
                                >
                                    {t("editor.insert_row_above")}
                                </button>
                                <button
                                    type="button"
                                    disabled={!editor.isActive("table")}
                                    onClick={() => {
                                        editor
                                            .chain()
                                            .focus()
                                            .addRowAfter()
                                            .run();
                                        setActiveDropdown(null);
                                    }}
                                    className="w-full text-left px-2.5 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-white/10 disabled:opacity-40 text-slate-700 dark:text-zinc-200"
                                >
                                    {t("editor.insert_row_below")}
                                </button>
                                <button
                                    type="button"
                                    disabled={!editor.isActive("table")}
                                    onClick={() => {
                                        editor
                                            .chain()
                                            .focus()
                                            .addColumnBefore()
                                            .run();
                                        setActiveDropdown(null);
                                    }}
                                    className="w-full text-left px-2.5 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-white/10 disabled:opacity-40 text-slate-700 dark:text-zinc-200"
                                >
                                    {t("editor.insert_column_before")}
                                </button>
                                <button
                                    type="button"
                                    disabled={!editor.isActive("table")}
                                    onClick={() => {
                                        editor
                                            .chain()
                                            .focus()
                                            .addColumnAfter()
                                            .run();
                                        setActiveDropdown(null);
                                    }}
                                    className="w-full text-left px-2.5 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-white/10 disabled:opacity-40 text-slate-700 dark:text-zinc-200"
                                >
                                    {t("editor.insert_column_after")}
                                </button>
                                <div className="h-px bg-slate-200 dark:bg-white/10 my-1" />
                                <button
                                    type="button"
                                    disabled={!editor.isActive("table")}
                                    onClick={() => {
                                        editor
                                            .chain()
                                            .focus()
                                            .deleteRow()
                                            .run();
                                        setActiveDropdown(null);
                                    }}
                                    className="w-full text-left px-2.5 py-1.5 rounded hover:bg-red-50 dark:hover:bg-red-950/30 text-red-600 disabled:opacity-40"
                                >
                                    {t("editor.delete_row")}
                                </button>
                                <button
                                    type="button"
                                    disabled={!editor.isActive("table")}
                                    onClick={() => {
                                        editor
                                            .chain()
                                            .focus()
                                            .deleteColumn()
                                            .run();
                                        setActiveDropdown(null);
                                    }}
                                    className="w-full text-left px-2.5 py-1.5 rounded hover:bg-red-50 dark:hover:bg-red-950/30 text-red-600 disabled:opacity-40"
                                >
                                    {t("editor.delete_column")}
                                </button>
                                <button
                                    type="button"
                                    disabled={!editor.isActive("table")}
                                    onClick={() => {
                                        editor
                                            .chain()
                                            .focus()
                                            .deleteTable()
                                            .run();
                                        setActiveDropdown(null);
                                    }}
                                    className="w-full text-left px-2.5 py-1.5 rounded hover:bg-red-50 dark:hover:bg-red-950/30 text-red-600 font-bold disabled:opacity-40"
                                >
                                    {t("editor.delete_table")}
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Main Icon Toolbar */}
                <div className="tiptap-menu-bar">
                    {/* Undo / Redo */}
                    <button
                        type="button"
                        className="editor-btn"
                        title={t("editor.undo")}
                        disabled={isSourceMode || !editor.can().undo()}
                        onClick={() => editor.chain().focus().undo().run()}
                    >
                        <Icon value="undo" size={15} />
                    </button>
                    <button
                        type="button"
                        className="editor-btn"
                        title={t("editor.redo")}
                        disabled={isSourceMode || !editor.can().redo()}
                        onClick={() => editor.chain().focus().redo().run()}
                    >
                        <Icon value="redo" size={15} />
                    </button>

                    <span className="divider" />

                    {/* Headings Dropdown */}
                    <div className="relative inline-block">
                        <button
                            type="button"
                            className={`editor-btn ${editor.isActive("heading") ? "is-active" : ""}`}
                            title={t("editor.heading")}
                            disabled={isSourceMode}
                            onClick={() => toggleDropdown("heading")}
                        >
                            <span className="text-xs font-bold mr-0.5">H</span>
                            <Icon value="chevronDown" size={10} />
                        </button>
                        {activeDropdown === "heading" && (
                            <div className="absolute left-0 top-full mt-1 w-36 rounded-lg bg-white dark:bg-dark-box shadow-xl border border-slate-200 dark:border-white/15 p-1 z-50 text-xs">
                                <button
                                    type="button"
                                    onClick={() => {
                                        editor
                                            .chain()
                                            .focus()
                                            .setParagraph()
                                            .run();
                                        setActiveDropdown(null);
                                    }}
                                    className="w-full text-left px-2.5 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-white/10"
                                >
                                    {t("editor.paragraph")}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        editor
                                            .chain()
                                            .focus()
                                            .toggleHeading({ level: 1 })
                                            .run();
                                        setActiveDropdown(null);
                                    }}
                                    className="w-full text-left px-2.5 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-white/10 font-bold text-sm"
                                >
                                    {t("editor.heading")} 1
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        editor
                                            .chain()
                                            .focus()
                                            .toggleHeading({ level: 2 })
                                            .run();
                                        setActiveDropdown(null);
                                    }}
                                    className="w-full text-left px-2.5 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-white/10 font-bold"
                                >
                                    {t("editor.heading")} 2
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        editor
                                            .chain()
                                            .focus()
                                            .toggleHeading({ level: 3 })
                                            .run();
                                        setActiveDropdown(null);
                                    }}
                                    className="w-full text-left px-2.5 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-white/10 font-semibold"
                                >
                                    {t("editor.heading")} 3
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Font Family Dropdown */}
                    <div className="relative inline-block">
                        <button
                            type="button"
                            className="editor-btn text-xs px-2 min-w-[70px] justify-between font-normal"
                            title={t("editor.font_family")}
                            disabled={isSourceMode}
                            onClick={() => toggleDropdown("fontFamily")}
                        >
                            <span className="truncate max-w-[65px]">
                                {((
                                    editor.getAttributes("textStyle") as Record<
                                        string,
                                        unknown
                                    >
                                ).fontFamily as string) || "Fuente"}
                            </span>
                            <Icon value="chevronDown" size={10} />
                        </button>
                        {activeDropdown === "fontFamily" && (
                            <div className="absolute left-0 top-full mt-1 w-44 rounded-lg bg-white dark:bg-dark-box shadow-xl border border-slate-200 dark:border-white/15 p-1 z-50 text-xs max-h-56 overflow-y-auto">
                                <button
                                    type="button"
                                    onClick={() => {
                                        editor
                                            .chain()
                                            .focus()
                                            .unsetFontFamily()
                                            .run();
                                        setActiveDropdown(null);
                                    }}
                                    className="w-full text-left px-2.5 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-white/10 text-slate-700 dark:text-zinc-200"
                                >
                                    Por defecto
                                </button>
                                {FONT_FAMILIES.map((font) => (
                                    <button
                                        key={font}
                                        type="button"
                                        style={{ fontFamily: font }}
                                        onClick={() => {
                                            editor
                                                .chain()
                                                .focus()
                                                .setFontFamily(font)
                                                .run();
                                            setActiveDropdown(null);
                                        }}
                                        className={`w-full text-left px-2.5 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-white/10 ${
                                            editor.isActive("textStyle", {
                                                fontFamily: font,
                                            })
                                                ? "bg-primary/10 text-primary font-bold"
                                                : "text-slate-700 dark:text-zinc-200"
                                        }`}
                                    >
                                        {font}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Font Size Dropdown */}
                    <div className="relative inline-block">
                        <button
                            type="button"
                            className="editor-btn text-xs px-2 min-w-[55px] justify-between font-normal"
                            title={t("editor.font_size")}
                            disabled={isSourceMode}
                            onClick={() => toggleDropdown("fontSize")}
                        >
                            <span>
                                {((
                                    editor.getAttributes("textStyle") as Record<
                                        string,
                                        unknown
                                    >
                                ).fontSize as string) || "Tam"}
                            </span>
                            <Icon value="chevronDown" size={10} />
                        </button>
                        {activeDropdown === "fontSize" && (
                            <div className="absolute left-0 top-full mt-1 w-32 rounded-lg bg-white dark:bg-dark-box shadow-xl border border-slate-200 dark:border-white/15 p-1 z-50 text-xs max-h-56 overflow-y-auto">
                                <button
                                    type="button"
                                    onClick={() => {
                                        editor
                                            .chain()
                                            .focus()
                                            .setMark("textStyle", {
                                                fontSize: null,
                                            })
                                            .removeEmptyTextStyle()
                                            .run();
                                        setActiveDropdown(null);
                                    }}
                                    className="w-full text-left px-2.5 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-white/10 text-slate-700 dark:text-zinc-200"
                                >
                                    Por defecto
                                </button>
                                {FONT_SIZES.map((size) => (
                                    <button
                                        key={size}
                                        type="button"
                                        onClick={() => {
                                            editor
                                                .chain()
                                                .focus()
                                                .setMark("textStyle", {
                                                    fontSize: size,
                                                })
                                                .run();
                                            setActiveDropdown(null);
                                        }}
                                        className={`w-full text-left px-2.5 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-white/10 ${
                                            editor.isActive("textStyle", {
                                                fontSize: size,
                                            })
                                                ? "bg-primary/10 text-primary font-bold"
                                                : "text-slate-700 dark:text-zinc-200"
                                        }`}
                                    >
                                        {size}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <button
                        type="button"
                        className="editor-btn"
                        title={t("editor.remove_format")}
                        disabled={isSourceMode}
                        onClick={() =>
                            editor
                                .chain()
                                .focus()
                                .unsetAllMarks()
                                .clearNodes()
                                .run()
                        }
                    >
                        <Icon value="removeFormatting" size={15} />
                    </button>

                    <span className="divider" />

                    {/* Basic Formatting: Bold, Italic, Underline, Strike */}
                    <button
                        type="button"
                        className={`editor-btn ${editor.isActive("bold") ? "is-active" : ""}`}
                        title={t("editor.bold")}
                        disabled={isSourceMode}
                        onClick={() =>
                            editor.chain().focus().toggleBold().run()
                        }
                    >
                        <Icon value="bold" size={15} />
                    </button>
                    <button
                        type="button"
                        className={`editor-btn ${editor.isActive("italic") ? "is-active" : ""}`}
                        title={t("editor.italic")}
                        disabled={isSourceMode}
                        onClick={() =>
                            editor.chain().focus().toggleItalic().run()
                        }
                    >
                        <Icon value="italic" size={15} />
                    </button>
                    <button
                        type="button"
                        className={`editor-btn ${editor.isActive("underline") ? "is-active" : ""}`}
                        title={t("editor.underline")}
                        disabled={isSourceMode}
                        onClick={() =>
                            editor.chain().focus().toggleUnderline().run()
                        }
                    >
                        <Icon value="underline" size={15} />
                    </button>
                    <button
                        type="button"
                        className={`editor-btn ${editor.isActive("strike") ? "is-active" : ""}`}
                        title={t("editor.strike")}
                        disabled={isSourceMode}
                        onClick={() =>
                            editor.chain().focus().toggleStrike().run()
                        }
                    >
                        <Icon value="strike" size={15} />
                    </button>

                    {/* Subscript & Superscript */}
                    <button
                        type="button"
                        className={`editor-btn ${editor.isActive("subscript") ? "is-active" : ""}`}
                        title={t("editor.subscript")}
                        disabled={isSourceMode}
                        onClick={() =>
                            editor.chain().focus().toggleSubscript().run()
                        }
                    >
                        <Icon value="subscript" size={15} />
                    </button>
                    <button
                        type="button"
                        className={`editor-btn ${editor.isActive("superscript") ? "is-active" : ""}`}
                        title={t("editor.superscript")}
                        disabled={isSourceMode}
                        onClick={() =>
                            editor.chain().focus().toggleSuperscript().run()
                        }
                    >
                        <Icon value="superscript" size={15} />
                    </button>

                    {/* Color Palettes */}
                    <div className="relative inline-block">
                        <button
                            type="button"
                            className="editor-btn"
                            title={t("editor.text_color")}
                            disabled={isSourceMode}
                            onClick={() => toggleDropdown("textColor")}
                        >
                            <Icon value="type" size={15} />
                        </button>
                        {activeDropdown === "textColor" && (
                            <div className="absolute left-0 top-full mt-1 p-2 rounded-xl bg-white dark:bg-dark-box shadow-xl border border-slate-200 dark:border-white/15 z-50 w-max">
                                <div className="grid grid-cols-10 gap-1 mb-2">
                                    {COLOR_PALETTE.map((color) => (
                                        <button
                                            key={color}
                                            type="button"
                                            onClick={() =>
                                                handleApplyColor(color, false)
                                            }
                                            className="w-4 h-4 rounded-sm border border-slate-300 hover:scale-125 transition-transform"
                                            style={{ backgroundColor: color }}
                                        />
                                    ))}
                                </div>
                                <button
                                    type="button"
                                    onClick={() =>
                                        handleApplyColor(null, false)
                                    }
                                    className="w-full text-center py-1 text-xs text-slate-600 dark:text-zinc-400 hover:bg-slate-100 dark:hover:bg-white/10 rounded"
                                >
                                    {t("editor.remove_format")}
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="relative inline-block">
                        <button
                            type="button"
                            className="editor-btn"
                            title={t("editor.background_color")}
                            disabled={isSourceMode}
                            onClick={() => toggleDropdown("bgColor")}
                        >
                            <Icon value="highlighter" size={15} />
                        </button>
                        {activeDropdown === "bgColor" && (
                            <div className="absolute left-0 top-full mt-1 p-2 rounded-xl bg-white dark:bg-dark-box shadow-xl border border-slate-200 dark:border-white/15 z-50 w-max">
                                <div className="grid grid-cols-10 gap-1 mb-2">
                                    {COLOR_PALETTE.map((color) => (
                                        <button
                                            key={color}
                                            type="button"
                                            onClick={() =>
                                                handleApplyColor(color, true)
                                            }
                                            className="w-4 h-4 rounded-sm border border-slate-300 hover:scale-125 transition-transform"
                                            style={{ backgroundColor: color }}
                                        />
                                    ))}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => handleApplyColor(null, true)}
                                    className="w-full text-center py-1 text-xs text-slate-600 dark:text-zinc-400 hover:bg-slate-100 dark:hover:bg-white/10 rounded"
                                >
                                    {t("editor.remove_format")}
                                </button>
                            </div>
                        )}
                    </div>

                    <span className="divider" />

                    {/* Alignment */}
                    <div className="relative inline-block">
                        <button
                            type="button"
                            className={`editor-btn ${editor.isActive({ textAlign: "center" }) || editor.isActive({ textAlign: "right" }) || editor.isActive({ textAlign: "justify" }) ? "is-active" : ""}`}
                            title={t("editor.alignment")}
                            disabled={isSourceMode}
                            onClick={() => toggleDropdown("align")}
                        >
                            <Icon value="alignLeft" size={15} />
                        </button>
                        {activeDropdown === "align" && (
                            <div className="absolute left-0 top-full mt-1 w-36 rounded-lg bg-white dark:bg-dark-box shadow-xl border border-slate-200 dark:border-white/15 p-1 z-50 text-xs">
                                <button
                                    type="button"
                                    onClick={() => {
                                        editor
                                            .chain()
                                            .focus()
                                            .setTextAlign("left")
                                            .run();
                                        setActiveDropdown(null);
                                    }}
                                    className="w-full text-left px-2.5 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-white/10 flex items-center gap-2"
                                >
                                    <Icon value="alignLeft" size={13} />
                                    <span>{t("editor.align_left")}</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        editor
                                            .chain()
                                            .focus()
                                            .setTextAlign("center")
                                            .run();
                                        setActiveDropdown(null);
                                    }}
                                    className="w-full text-left px-2.5 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-white/10 flex items-center gap-2"
                                >
                                    <Icon value="alignCenter" size={13} />
                                    <span>{t("editor.align_center")}</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        editor
                                            .chain()
                                            .focus()
                                            .setTextAlign("right")
                                            .run();
                                        setActiveDropdown(null);
                                    }}
                                    className="w-full text-left px-2.5 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-white/10 flex items-center gap-2"
                                >
                                    <Icon value="alignRight" size={13} />
                                    <span>{t("editor.align_right")}</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        editor
                                            .chain()
                                            .focus()
                                            .setTextAlign("justify")
                                            .run();
                                        setActiveDropdown(null);
                                    }}
                                    className="w-full text-left px-2.5 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-white/10 flex items-center gap-2"
                                >
                                    <Icon value="alignJustify" size={13} />
                                    <span>{t("editor.align_justify")}</span>
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Lists */}
                    <button
                        type="button"
                        className={`editor-btn ${editor.isActive("bulletList") ? "is-active" : ""}`}
                        title={t("editor.bullet_list")}
                        disabled={isSourceMode}
                        onClick={() =>
                            editor.chain().focus().toggleBulletList().run()
                        }
                    >
                        <Icon value="list" size={15} />
                    </button>
                    <button
                        type="button"
                        className={`editor-btn ${editor.isActive("orderedList") ? "is-active" : ""}`}
                        title={t("editor.ordered_list")}
                        disabled={isSourceMode}
                        onClick={() =>
                            editor.chain().focus().toggleOrderedList().run()
                        }
                    >
                        <Icon value="listOrdered" size={15} />
                    </button>

                    <button
                        type="button"
                        className={`editor-btn ${editor.isActive("blockquote") ? "is-active" : ""}`}
                        title={t("editor.blockquote")}
                        disabled={isSourceMode}
                        onClick={() =>
                            editor.chain().focus().toggleBlockquote().run()
                        }
                    >
                        <Icon value="quote" size={15} />
                    </button>
                    <button
                        type="button"
                        className={`editor-btn ${editor.isActive("codeBlock") ? "is-active" : ""}`}
                        title={t("editor.code_block")}
                        disabled={isSourceMode}
                        onClick={() =>
                            editor.chain().focus().toggleCodeBlock().run()
                        }
                    >
                        <Icon value="code" size={15} />
                    </button>

                    {/* Outdent & Indent */}
                    <button
                        type="button"
                        className="editor-btn"
                        title={t("editor.outdent")}
                        disabled={isSourceMode}
                        onClick={() =>
                            (
                                editor.commands as unknown as {
                                    outdent: () => boolean;
                                }
                            ).outdent?.()
                        }
                    >
                        <Icon value="outdent" size={15} />
                    </button>
                    <button
                        type="button"
                        className="editor-btn"
                        title={t("editor.indent")}
                        disabled={isSourceMode}
                        onClick={() =>
                            (
                                editor.commands as unknown as {
                                    indent: () => boolean;
                                }
                            ).indent?.()
                        }
                    >
                        <Icon value="indent" size={15} />
                    </button>
                    <button
                        type="button"
                        className="editor-btn"
                        title={t("editor.horizontal_line")}
                        disabled={isSourceMode}
                        onClick={() =>
                            editor.chain().focus().setHorizontalRule().run()
                        }
                    >
                        <Icon value="minus" size={15} />
                    </button>

                    <span className="divider" />

                    {/* Link */}
                    <button
                        type="button"
                        className={`editor-btn ${editor.isActive("link") ? "is-active" : ""}`}
                        title={t("editor.link")}
                        disabled={isSourceMode}
                        onClick={openLinkDialog}
                    >
                        <Icon value="link" size={15} />
                    </button>
                    <button
                        type="button"
                        className="editor-btn"
                        title={t("editor.unlink")}
                        disabled={isSourceMode || !editor.isActive("link")}
                        onClick={() => editor.chain().focus().unsetLink().run()}
                    >
                        <Icon value="unlink" size={15} />
                    </button>

                    {/* Image */}
                    <button
                        type="button"
                        className="editor-btn"
                        title={t("editor.image")}
                        disabled={isSourceMode}
                        onClick={() => {
                            setImageUrlInput("");
                            setImageModalOpen(true);
                            setActiveDropdown(null);
                        }}
                    >
                        <Icon value="image" size={15} />
                    </button>

                    {/* Interactive Table Picker */}
                    <div className="relative inline-block">
                        <button
                            type="button"
                            className={`editor-btn ${editor.isActive("table") ? "is-active" : ""}`}
                            title={t("editor.table")}
                            disabled={isSourceMode}
                            onClick={() => toggleDropdown("table")}
                        >
                            <Icon value="table" size={15} />
                        </button>
                        {activeDropdown === "table" && (
                            <div className="w-max absolute left-0 top-full mt-1 p-2 rounded-xl bg-white dark:bg-dark-box shadow-xl border border-slate-200 dark:border-white/15 z-50">
                                <div className="text-center text-xs font-semibold text-slate-600 dark:text-zinc-300 mb-1.5">
                                    {hoveredTable.rows > 0
                                        ? `${hoveredTable.rows} x ${hoveredTable.cols}`
                                        : t("editor.insert_table")}
                                </div>
                                <div className="grid grid-cols-8 gap-1">
                                    {Array.from({ length: 64 }).map((_, i) => {
                                        const r = Math.floor(i / 8) + 1;
                                        const c = (i % 8) + 1;
                                        const isHov =
                                            r <= hoveredTable.rows &&
                                            c <= hoveredTable.cols;
                                        return (
                                            <div
                                                key={i}
                                                onMouseEnter={() =>
                                                    setHoveredTable({
                                                        rows: r,
                                                        cols: c,
                                                    })
                                                }
                                                onClick={() =>
                                                    handleInsertTable(r, c)
                                                }
                                                className={`w-3.5 h-3.5 border rounded-xs cursor-pointer transition-colors ${
                                                    isHov
                                                        ? "bg-primary border-primary"
                                                        : "border-slate-300 dark:border-white/20"
                                                }`}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Symbols Dropdown */}
                    <div className="relative inline-block">
                        <button
                            type="button"
                            className="editor-btn text-xs font-semibold"
                            title={t("editor.symbol")}
                            disabled={isSourceMode}
                            onClick={() => toggleDropdown("symbol")}
                        >
                            <span>Ω</span>
                        </button>
                        {activeDropdown === "symbol" && (
                            <div className="absolute right-0 top-full mt-1 p-2 rounded-xl bg-white dark:bg-dark-box shadow-xl border border-slate-200 dark:border-white/15 z-50 max-w-xs max-h-52 overflow-y-auto grid grid-cols-7 gap-1 w-max">
                                {COMMON_SYMBOLS.map((s) => (
                                    <button
                                        key={s}
                                        type="button"
                                        onClick={() => {
                                            editor
                                                .chain()
                                                .focus()
                                                .insertContent(s)
                                                .run();
                                            setActiveDropdown(null);
                                        }}
                                        className="h-7 w-7 text-sm flex items-center justify-center rounded hover:bg-slate-100 dark:hover:bg-white/10"
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Emoji Dropdown */}
                    <div className="relative inline-block">
                        <button
                            type="button"
                            className="editor-btn"
                            title={t("editor.emoji")}
                            disabled={isSourceMode}
                            onClick={() => toggleDropdown("emoji")}
                        >
                            <Icon value="smile" size={15} />
                        </button>
                        {activeDropdown === "emoji" && (
                            <div className="absolute right-0 top-full mt-1 p-2 rounded-xl bg-white dark:bg-dark-box shadow-xl border border-slate-200 dark:border-white/15 z-50 max-w-xs max-h-52 overflow-y-auto grid grid-cols-7 gap-1 w-max">
                                {COMMON_EMOJIS.map((e, idx) => (
                                    <button
                                        key={`${e}-${idx}`}
                                        type="button"
                                        onClick={() => {
                                            editor
                                                .chain()
                                                .focus()
                                                .insertContent(e)
                                                .run();
                                            setActiveDropdown(null);
                                        }}
                                        className="h-7 w-7 text-base flex items-center justify-center rounded hover:bg-slate-100 dark:hover:bg-white/10 hover:scale-110 transition-transform"
                                    >
                                        {e}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <span className="divider" />

                    {/* HTML Source Toggle */}
                    <button
                        type="button"
                        className={`editor-btn ${isSourceMode ? "is-active" : ""}`}
                        title={
                            isSourceMode
                                ? t("editor.rich_text")
                                : t("editor.source_code")
                        }
                        onClick={toggleSourceMode}
                    >
                        <Icon value="code" size={15} />
                    </button>

                    {/* Fullscreen Toggle */}
                    {!hideFullscreen && (
                        <button
                            type="button"
                            className="editor-btn"
                            title={t("editor.fullscreen")}
                            onClick={() => {
                                if (onFullscreenClick) onFullscreenClick();
                                else toggleFullScreen();
                            }}
                        >
                            <Icon
                                value={isFullScreen ? "minimize" : "maximize"}
                                size={15}
                            />
                        </button>
                    )}
                </div>
            </div>

            {/* Editor Body */}
            <div
                className={`tiptap-editor-box flex-1 flex flex-col overflow-hidden bg-white dark:bg-dark-box${rounded ? " rounded-b-2xl" : ""}`}
            >
                {isSourceMode ? (
                    <div className="flex-1 flex flex-col p-3 bg-slate-900 text-slate-100 font-mono text-xs">
                        <textarea
                            value={sourceContent}
                            onChange={(e) => setSourceContent(e.target.value)}
                            className="flex-1 w-full bg-transparent border-0 outline-none resize-none"
                            placeholder="HTML..."
                        />
                    </div>
                ) : (
                    <div
                        className="flex-1 flex flex-col overflow-y-auto cursor-text"
                        style={{ minHeight }}
                        onClick={() => {
                            if (editor && !editor.isFocused) {
                                editor.commands.focus();
                            }
                        }}
                    >
                        <EditorContent
                            editor={editor}
                            className="flex-1 flex flex-col"
                        />
                    </div>
                )}
            </div>

            {/* Link Modal */}
            <Modal
                isOpen={linkModalOpen}
                onClose={() => setLinkModalOpen(false)}
                title={t("editor.link")}
                maxWidth="sm"
                onSubmit={applyLink}
                submitLabel={t("editor.apply")}
                cancelLabel={t("editor.cancel")}
                zIndex={2000}
            >
                <div className="space-y-4">
                    <Input
                        label={t("editor.text_to_display")}
                        value={linkText}
                        onChange={(e) => setLinkText(e.target.value)}
                        placeholder="Texto..."
                    />
                    <Input
                        label={t("editor.link_url")}
                        value={linkUrl}
                        onChange={(e) => setLinkUrl(e.target.value)}
                        placeholder="https://..."
                        autoFocus
                    />
                    <Switch
                        label={t("editor.open_new_window")}
                        checked={linkNewTab}
                        onChange={(val) => setLinkNewTab(val)}
                        position="left"
                    />
                </div>
            </Modal>

            {/* Image Modal */}
            <Modal
                isOpen={imageModalOpen}
                onClose={() => setImageModalOpen(false)}
                title={t("editor.insert_image")}
                maxWidth="sm"
                onSubmit={handleInsertImage}
                submitLabel={t("editor.apply")}
                cancelLabel={t("editor.cancel")}
                submitDisabled={!imageUrlInput.trim()}
                zIndex={2000}
            >
                <div className="space-y-4">
                    <Input
                        label={t("editor.image_url")}
                        value={imageUrlInput}
                        onChange={(e) => setImageUrlInput(e.target.value)}
                        placeholder="https://..."
                        autoFocus
                    />
                    <div>
                        <label className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1 ">
                            {t("editor.upload_image")}
                        </label>
                        <div
                            onDragOver={handleImageDragOver}
                            onDragLeave={handleImageDragLeave}
                            onDrop={handleImageDrop}
                            onClick={() =>
                                document
                                    .getElementById("rich-text-image-upload")
                                    ?.click()
                            }
                            className={`relative flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-xl transition-colors cursor-pointer ${
                                isImageDragOver
                                    ? "border-primary bg-primary/5"
                                    : "border-slate-300 dark:border-white/20 bg-slate-50 dark:bg-zinc-800/50 hover:bg-slate-100 dark:hover:bg-zinc-800"
                            }`}
                        >
                            <Icon
                                value="upload"
                                size={24}
                                className={
                                    isImageDragOver
                                        ? "text-primary"
                                        : "text-slate-400"
                                }
                            />
                            <span className="mt-2 text-xs font-medium text-slate-600 dark:text-zinc-400 text-center">
                                {t("common.drop_files")}
                            </span>
                            <input
                                id="rich-text-image-upload"
                                type="file"
                                accept="image/*"
                                onChange={handleImageFileChange}
                                className="hidden"
                            />
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );

    if (isFullScreen && typeof document !== "undefined") {
        return (
            <Modal
                isOpen={true}
                onClose={toggleFullScreen}
                isFullscreen
                onFullscreenChange={(v) => !v && toggleFullScreen()}
                zIndex={120000}
                effect="fade"
                hideClose
                bodyClassName="p-0 min-h-0 flex-1 flex flex-col"
            >
                {editorContent}
            </Modal>
        );
    }

    return editorContent;
};

export default RichTextFull;
